import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import BaseListItem from '../BaseListItem';
import { LinkSmall } from '@styled';
import getFavoriteHook from '@hooks/getFavoriteHook';
import useIcscTracking from '@tracking/useIcscTracking';

const MemberListItem = ({
    id,
    firstName,
    lastName,
    fullName,
    companyID,
    companyName,
    title,
    designations,
    displayLocation,
    businessType,
    photoURL,
    url,
    isFavorite: initialIsFavorite,
    onClick = () => {},
    isLoggedIn = true,
}) => {
    const history = useHistory();
    const { trackEvent } = useIcscTracking();
    const useFavorite = getFavoriteHook({ type: 'contact', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite } = useFavorite();
    const [isRemoved, setIsRemoved] = useState(false);

    const onFavorite = () => {
        if (!isFavorite || isRemoved) {
            setIsRemoved(false);
            saveFavorite(id);
            return;
        }
        if (isFavorite) {
            setIsRemoved(true);
            removeFavorite(id);
        }
    };

    return (
        <BaseListItem
            id={id}
            type="member"
            title={fullName}
            url={url}
            isFavorite={isFavorite}
            onFavorite={onFavorite}
            isLoadingFavorite={isLoadingFavorite}
            isLoggedIn={isLoggedIn}
            showShareIcon={false}
            imageBlock={
                <>
                    {photoURL && <img src={photoURL} alt={fullName} className="p-0 card-img-right profile-img-list" />}
                    {!photoURL && (
                        <div
                            className="p-0 member-default card-img-right profile-img-list d-flex align-items-center"
                            alt={fullName}
                        >
                            <div className="letters mx-auto">
                                <span className="display7">{firstName.slice(0, 1)}</span>
                                <span className="display7">{lastName.slice(0, 1)}</span>
                            </div>
                        </div>
                    )}
                </>
            }
            contentBlock={
                <>
                    <a href={url} onClick={() => onClick(url)} onKeyDown={() => onClick(url)} tabIndex="0">
                        <h3 className="card-title mt-1 mb-0 ml-0 mx-0 py-0 display8 bold">
                            {fullName}
                            {designations && <span>{`, ${designations}`}</span>}
                        </h3>
                    </a>
                    <p className="display9 secondary mt-1 mb-0">{title}</p>
                    <p className="display9 secondary mt-1 mb-0">{displayLocation}</p>

                    {(companyID || companyName) && (
                        <LinkSmall
                            href={`/company/profile/${companyID}`}
                            className="text-sm"
                            onClick={e => {
                                onClick();

                                // History is undefined if called from within a modal
                                if (!history) {
                                    return;
                                }

                                e.preventDefault();
                                history.push(`/company/profile/${companyID}`, { state: { inApp: true } });
                            }}
                        >
                            {companyName + (businessType && businessType.length > 0 ? ` (${businessType[0]})` : '')}
                        </LinkSmall>
                    )}
                </>
            }
            onMessage={() => {
                trackEvent({
                    type: 'click',
                    target: 'send_a_message_modal_mail_icon_button',
                    value: `Messaged ${firstName} ${lastName}`,
                });
            }}
        />
    );
};

MemberListItem.propTypes = {
    id: PropTypes.number.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    companyID: PropTypes.number,
    companyName: PropTypes.string,
    title: PropTypes.string,
    designations: PropTypes.string.isRequired,
    displayLocation: PropTypes.string,
    businessType: PropTypes.array.isRequired,
    photoURL: PropTypes.string,
    isFavorite: PropTypes.bool.isRequired,
    onClick: PropTypes.func,
    url: PropTypes.string,
    isLoggedIn: PropTypes.bool,
};

export default MemberListItem;
